import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from '@ibk/shared-translation';
import { Button, Icon } from '@ibk/shared-ui';
import { PENDING_PAYMENT_NOTICES_LIMIT } from '../../utils/pendingPaymentNotices';
import styles from './PendingPaymentNotices.module.css';
const REFERENCE_TOKEN = '{{reference}}';
const DRAG_THRESHOLD = 5;
function resolveKeyboardIndex(key, currentIndex, lastIndex) {
    switch (key) {
        case 'ArrowRight':
            return Math.min(currentIndex + 1, lastIndex);
        case 'ArrowLeft':
            return Math.max(currentIndex - 1, 0);
        case 'Home':
            return 0;
        case 'End':
            return lastIndex;
        default:
            return null;
    }
}
function splitAroundReference(template) {
    const tokenIndex = template.indexOf(REFERENCE_TOKEN);
    if (tokenIndex < 0) {
        return [template, ''];
    }
    return [template.slice(0, tokenIndex), template.slice(tokenIndex + REFERENCE_TOKEN.length)];
}
function matchDragOrigin(dragOrigin, event) {
    return dragOrigin?.pointerId === event.pointerId ? dragOrigin : null;
}
function getNotices(viewport) {
    return Array.from(viewport.querySelectorAll('[data-pending-payment-notice]'));
}
function getScrollTarget(viewport, notice) {
    return Math.min(notice?.offsetLeft ?? 0, Math.max(viewport.scrollWidth - viewport.clientWidth, 0));
}
function findClosestIndex(viewport) {
    const notices = getNotices(viewport);
    return notices.reduce((closestIndex, notice, index) => Math.abs(getScrollTarget(viewport, notice) - viewport.scrollLeft) <
        Math.abs(getScrollTarget(viewport, notices[closestIndex]) - viewport.scrollLeft)
        ? index
        : closestIndex, 0);
}
function scrollToIndex(viewport, index) {
    viewport.scrollTo?.({ left: getScrollTarget(viewport, getNotices(viewport)[index]), behavior: 'smooth' });
}
export function PendingPaymentNotices({ orders, onPay }) {
    const { t } = useTranslation();
    const viewportRef = useRef(null);
    const dragOriginRef = useRef(null);
    const hasDraggedRef = useRef(false);
    const [activeIndex, setActiveIndex] = useState(0);
    const [isDragging, setIsDragging] = useState(false);
    const visibleOrders = orders.slice(0, PENDING_PAYMENT_NOTICES_LIMIT);
    const isCarousel = visibleOrders.length > 1;
    useEffect(() => {
        setActiveIndex(currentIndex => Math.min(currentIndex, Math.max(visibleOrders.length - 1, 0)));
    }, [visibleOrders.length]);
    useEffect(() => {
        const viewport = viewportRef.current;
        if (!viewport) {
            return undefined;
        }
        const handleScroll = () => {
            setActiveIndex(findClosestIndex(viewport));
        };
        const handlePointerDown = (event) => {
            if (!isCarousel || event.pointerType === 'touch' || event.button !== 0) {
                return;
            }
            dragOriginRef.current = {
                pointerId: event.pointerId,
                pointerX: event.clientX,
                scrollLeft: viewport.scrollLeft,
            };
            hasDraggedRef.current = false;
        };
        const handlePointerMove = (event) => {
            const dragOrigin = matchDragOrigin(dragOriginRef.current, event);
            if (!dragOrigin) {
                return;
            }
            if (event.buttons === 0) {
                dragOriginRef.current = null;
                return;
            }
            const draggedDistance = event.clientX - dragOrigin.pointerX;
            if (!hasDraggedRef.current) {
                if (Math.abs(draggedDistance) < DRAG_THRESHOLD) {
                    return;
                }
                hasDraggedRef.current = true;
                viewport.setPointerCapture?.(event.pointerId);
                globalThis.getSelection?.()?.removeAllRanges();
                setIsDragging(true);
            }
            viewport.scrollLeft = dragOrigin.scrollLeft - draggedDistance;
        };
        const handlePointerUp = (event) => {
            if (!matchDragOrigin(dragOriginRef.current, event)) {
                return;
            }
            dragOriginRef.current = null;
            if (viewport.hasPointerCapture?.(event.pointerId)) {
                viewport.releasePointerCapture(event.pointerId);
            }
            setIsDragging(false);
            if (hasDraggedRef.current) {
                const closestIndex = findClosestIndex(viewport);
                setActiveIndex(closestIndex);
                scrollToIndex(viewport, closestIndex);
            }
        };
        const handleClickCapture = (event) => {
            if (!hasDraggedRef.current) {
                return;
            }
            hasDraggedRef.current = false;
            event.preventDefault();
            event.stopPropagation();
        };
        const handleDragStart = (event) => {
            event.preventDefault();
        };
        viewport.addEventListener('scroll', handleScroll);
        viewport.addEventListener('pointerdown', handlePointerDown);
        viewport.addEventListener('pointermove', handlePointerMove);
        viewport.addEventListener('pointerup', handlePointerUp);
        viewport.addEventListener('pointercancel', handlePointerUp);
        viewport.addEventListener('click', handleClickCapture, true);
        viewport.addEventListener('dragstart', handleDragStart);
        return () => {
            viewport.removeEventListener('scroll', handleScroll);
            viewport.removeEventListener('pointerdown', handlePointerDown);
            viewport.removeEventListener('pointermove', handlePointerMove);
            viewport.removeEventListener('pointerup', handlePointerUp);
            viewport.removeEventListener('pointercancel', handlePointerUp);
            viewport.removeEventListener('click', handleClickCapture, true);
            viewport.removeEventListener('dragstart', handleDragStart);
        };
    }, [isCarousel]);
    if (visibleOrders.length === 0) {
        return null;
    }
    const goToIndex = (index) => {
        const viewport = viewportRef.current;
        if (!viewport) {
            return;
        }
        setActiveIndex(index);
        scrollToIndex(viewport, index);
    };
    const handleDotKeyDown = (event, index) => {
        const nextIndex = resolveKeyboardIndex(event.key, index, visibleOrders.length - 1);
        if (nextIndex === null) {
            return;
        }
        event.preventDefault();
        goToIndex(nextIndex);
        event.currentTarget.parentElement?.children[nextIndex]?.focus();
    };
    return (_jsxs("section", { className: styles.container, "aria-label": t('benefits.home.pending-payment.section-label'), children: [_jsx("div", { ref: viewportRef, className: `${styles.viewport} ${isCarousel ? styles.viewportDraggable : ''} ${isDragging ? styles.viewportDragging : ''}`.trim(), "data-testid": 'viewHomePageEmissorPendingPaymentNotices', children: _jsx("div", { className: `${styles.track} ${isCarousel ? styles.trackCarousel : ''}`.trim(), children: visibleOrders.map(order => {
                        const identifier = order.description?.trim();
                        const reference = identifier || order.protocol;
                        const [messageStart, messageEnd] = splitAroundReference(t(identifier
                            ? 'benefits.home.pending-payment.message-with-description'
                            : 'benefits.home.pending-payment.message-with-protocol'));
                        return (_jsxs("article", { className: styles.notice, "data-pending-payment-notice": true, "data-testid": `viewPendingPaymentNotice-${order.id}`, children: [_jsx("span", { className: styles.iconContainer, "aria-hidden": true, children: _jsx(Icon, { name: 'digitalAccountFilled', className: styles.icon }) }), _jsxs("p", { className: styles.message, children: [messageStart, _jsx("strong", { className: styles.reference, title: reference, children: reference }), messageEnd] }), _jsx(Button, { variant: 'secondary', size: 'small', dataTestId: `buttonPendingPaymentNoticePay-${order.id}`, className: styles.payButton, onClick: () => onPay(order), children: t('benefits.home.pending-payment.pay-now') })] }, order.id));
                    }) }) }), isCarousel && (_jsx("div", { className: styles.dots, "data-testid": 'viewPendingPaymentNoticesPagination', children: visibleOrders.map((order, index) => (_jsx("button", { type: 'button', className: `${styles.dot} ${index === activeIndex ? styles.dotActive : ''}`, "aria-label": t('benefits.home.pending-payment.go-to-notice', { position: index + 1 }), "aria-current": index === activeIndex ? 'true' : undefined, onClick: () => goToIndex(index), onKeyDown: event => handleDotKeyDown(event, index), "data-testid": `buttonPendingPaymentNoticeDot-${index}` }, order.id))) }))] }));
}
